import { Link } from "react-router-dom";
import LogoMark from "../../Components/Brand/LogoMark";
import LoopWatermark from "../../Components/Brand/LoopWatermark";
import { ArrowIcon } from "../../Components/Brand/Icons";
import Showcase from "../../Components/Showcase/Showcase";
import RouteLine from "../../Components/Track/RouteLine";
import ScreenshotFrame from "../../Components/ServiceGraphics/ScreenshotFrame";
import FixGraphic from "../../Components/ServiceGraphics/FixGraphic";
import PhonePairFrame from "../../Components/ServiceGraphics/PhonePairFrame";
import AdminPanelGraphic from "../../Components/ServiceGraphics/AdminPanelGraphic";
import { CASE_STUDIES } from "../../content/work";
import { INDUSTRIES, TESTIMONIALS } from "../../content/clients";
import StationTrack from "../../Components/Track/StationTrack";
import Seo from "../../seo/Seo";
import { breadcrumbSchema } from "../../seo/site";
import "./work.css";

/** Text in [brackets] is a placeholder the owner still has to supply. */
const isPlaceholder = (text: string) => text.startsWith("[");

const Fact = ({ label, text }: { label: string; text: string }) => (
  <div className="is-case-facts__row">
    <dt>{label}</dt>
    <dd className={isPlaceholder(text) ? "is-placeholder" : undefined}>{text}</dd>
  </div>
);

/** Quote with one emphasised phrase anywhere in it. */
const Quote = ({ text, emphasis }: { text: string; emphasis?: string }) => {
  if (!emphasis || !text.includes(emphasis)) return <>“{text}”</>;
  const [before, after] = text.split(emphasis);
  return (
    <>
      “{before}
      <strong>{emphasis}</strong>
      {after}”
    </>
  );
};

const WorkPage = () => {
  const trvlar = TESTIMONIALS.find((t) => t.author === "Trvlar");
  const vera = TESTIMONIALS.find((t) => t.author === "Vera InkJet");

  return (
    <>
      {/* noindex until the case studies have real content (see CLAUDE.md › SEO) */}
      <Seo
        path="/work"
        noindex
        title="Our Work — Web & Mobile Projects | Infinitude Stack"
        description="Web and mobile projects for travel & hospitality, fashion, automotive and commerce clients — Trvlar, Nicole Benisti, JVM Auto, VRT Solutions, Vera InkJet."
        schema={[breadcrumbSchema("Work", "/work")]}
      />

      <div className="is-work">
        {/* ---------- Hero ---------- */}
        <section className="is-container is-work-hero">
          <h1 className="is-display is-work-hero__title">
            Products we’ve helped <strong className="is-accent-text">build.</strong>
          </h1>
          <p className="is-lede is-work-hero__lede">
            A few of the teams we’ve designed, built and grown software with — from
            early-stage startups to established businesses.
          </p>
          <RouteLine
            size="lg"
            label="Jump to a project"
            stops={CASE_STUDIES.map((c, i) => ({
              num: String(i + 1).padStart(2, "0"),
              label: c.client,
              href: `#${c.id}`,
            }))}
          />
        </section>

        {/* ---------- Case studies: scroll-driven showcase ---------- */}
        <Showcase
          label="Case studies"
          items={CASE_STUDIES.map((c) => ({
            id: c.id,
            title: c.client,
            visual:
              c.frame === "fix" ? (
                <FixGraphic kind={c.fix ?? "cart"} />
              ) : c.frame === "phones" ? (
                <PhonePairFrame
                  name={c.client}
                  front={c.screens?.phone ?? ""}
                  back={c.screens?.phoneBack ?? ""}
                  livePlate={c.livePlate}
                />
              ) : c.frame === "admin" ? (
                <AdminPanelGraphic />
              ) : (
              <ScreenshotFrame
                name={c.client}
                domain={c.domain}
                frame={c.frame === "web" ? "web" : "web-phone"}
                web={c.screens?.web}
                phone={c.screens?.phone}
              />
              ),
            body: (
              <>
                <p className={"is-showcase__text" + (isPlaceholder(c.summary) ? " is-placeholder" : "")}>
                  {c.summary}
                </p>
                <RouteLine label={`${c.client}: services`} stops={c.services.map((s) => ({ label: s }))} />
                <dl className="is-case-facts">
                  <Fact label="The challenge" text={c.challenge} />
                  <Fact label="What we built" text={c.built} />
                  <Fact label="The result" text={c.result} />
                </dl>
              </>
            ),
          }))}
        />

        {/* ---------- Industries ---------- */}
        <section className="is-container is-work-industries" aria-labelledby="work-industries-title">
          <div className="is-section-head is-reveal">
            <div>
              <p className="is-eyebrow">Industries</p>
              <h2 id="work-industries-title" className="is-display is-h2">
                Different industries, <strong className="is-accent-text">one way of working.</strong>
              </h2>
            </div>
          </div>
          <StationTrack items={INDUSTRIES} />
        </section>

        {/* ---------- In their words ---------- */}
        <section className="is-work-words" aria-labelledby="work-words-title">
          <LoopWatermark />
          <div className="is-container">
            <div className="is-section-head is-section-head--dark is-reveal">
              <div>
                <p className="is-eyebrow">In their words</p>
                <h2 id="work-words-title" className="is-display is-h2">
                  What it’s like to <strong>work with us.</strong>
                </h2>
              </div>
            </div>
            <div className="is-work-words__quotes">
              {trvlar?.full && (
                <figure className="is-work-quote is-work-quote--main is-reveal">
                  <LogoMark size={48} stroke="var(--is-text-on-dark)" strokeWidth={4} />
                  <blockquote>
                    <Quote text={trvlar.full} emphasis="handled challenges with ease." />
                  </blockquote>
                  <figcaption>
                    <span className="is-work-quote__line" />
                    <span className="is-work-quote__author">{trvlar.author}</span>
                    <span className="is-work-quote__role">{trvlar.role}</span>
                  </figcaption>
                </figure>
              )}
              {vera && (
                <figure className="is-work-quote is-work-quote--side is-reveal">
                  <blockquote>
                    <Quote text={vera.quote} emphasis={vera.emphasis} />
                  </blockquote>
                  <figcaption>
                    <span className="is-work-quote__line" />
                    <span className="is-work-quote__author">{vera.author}</span>
                    <span className="is-work-quote__role">{vera.role}</span>
                  </figcaption>
                </figure>
              )}
            </div>
          </div>
        </section>

        {/* ---------- CTA: rounded navy panel ---------- */}
        <section className="is-work-cta" aria-labelledby="work-cta-title">
          <div className="is-work-cta__panel is-reveal">
            <h2 id="work-cta-title" className="is-display is-work-cta__title">
              Your project could be <strong>next.</strong>
            </h2>
            <Link to="/contact" className="is-btn is-btn--accent">
              Start a project
              <span className="is-btn__arrow">
                <ArrowIcon />
              </span>
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default WorkPage;
